/**
 * Public API — unauthenticated endpoints used by the marketing site.
 * Backed by GET /api/v1/public/* (no token required).
 */
import api from './api.service';

export interface PublicPlan {
  name: string;
  displayName: string;
  description: string;
  /** Monthly price in `currency`. */
  price: number;
  /** Yearly price, or null when the plan isn't billed yearly. */
  yearlyPrice: number | null;
  currency: string;
  billingCycle: 'monthly' | 'yearly' | 'both';
  isPopular: boolean;
  sortOrder: number;
  /** Server-derived selling points for the pricing card. */
  highlights: string[];
  /** Curated per-plan entitlements for the "Compare all features" table.
   *  Optional so a cached/older response without it degrades gracefully. */
  features?: PublicPlanFeatures;
}

/** Marketing-safe subset of a plan's entitlements. Booleans are on/off;
 *  numeric limits use 0 = unlimited. */
export interface PublicPlanFeatures {
  whatsappEnabled: boolean;
  instagramEnabled: boolean;
  campaignsEnabled: boolean;
  workflowsEnabled: boolean;
  enableRag: boolean;
  llmConfigEnabled: boolean;
  apiAccess: boolean;
  webhooksEnabled: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  agentsLimit: number;
  contactsLimit: number;
  messagesLimit: number;
  usersLimit: number;
  aiTokensLimit: number;
  storageLimit: number;
}

/**
 * Module-level cache + in-flight de-dupe. Two near-simultaneous callers
 * (e.g. React 18 StrictMode's mount→cleanup→mount in dev, or Pricing
 * sections mounting on more than one page) share a single network request;
 * once resolved, plans are served from cache for PLANS_CACHE_TTL_MS since
 * pricing doesn't change intra-session.
 */
const PLANS_CACHE_TTL_MS = 5 * 60 * 1000;
let plansCache: { data: PublicPlan[]; expiresAt: number } | null = null;
let plansInFlight: Promise<PublicPlan[]> | null = null;

/** Active subscription plans (safe subset) for the homepage pricing section. */
export async function getPublicPlans(): Promise<PublicPlan[]> {
  if (plansCache && plansCache.expiresAt > Date.now()) return plansCache.data;
  if (plansInFlight) return plansInFlight;

  plansInFlight = (async () => {
    try {
      const body = await api.get<{ success: boolean; data: PublicPlan[] }>('/public/plans');
      const data = body?.data ?? [];
      plansCache = { data, expiresAt: Date.now() + PLANS_CACHE_TTL_MS };
      return data;
    } finally {
      plansInFlight = null;
    }
  })();
  return plansInFlight;
}

export interface EnquiryPayload {
  fullName: string;
  workEmail: string;
  /** digits only, with country code, no leading + */
  phone: string;
  companyName: string;
  companySize?: string;
  role?: string;
  country?: string;
  messageVolume?: string;
  message?: string;
  planInterest?: string;
  howHeard?: string;
}

/** Contact Us submission — becomes a platform enquiry visible only to super admin. */
export async function submitEnquiry(
  payload: EnquiryPayload
): Promise<{ success: boolean; message: string; enquiryId: string }> {
  return api.post('/public/enquiries', payload);
}
